import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '@byscott-io/corebyscott';
import { useParams, Link } from 'react-router-dom';
import { apps, quota } from '../api/client';

export default function QuotaPage() {
  const { appId } = useParams();
  const [app, setApp] = useState(null);
  const [quotaList, setQuotaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingQuotum, setEditingQuotum] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [appRes, quotaRes] = await Promise.all([
        apps.get(appId),
        quota.list(appId),
      ]);
      setApp(appRes.data.app);
      setQuotaList(quotaRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async (q) => {
    if (!confirm(`Delete quota "${q.name}"?`)) return;
    try {
      await quota.delete(appId, q.id);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete quota');
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading...</div>;
  if (error) return <div className="p-6"><div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div></div>;

  return (
    <div className="p-6">
      <div className="mb-6 text-sm">
        <Link to="/app/apps" className="text-blue-600 hover:text-blue-800">Apps</Link>
        <span className="mx-2 text-gray-400">/</span>
        <Link to={`/app/apps/${appId}`} className="text-blue-600 hover:text-blue-800">{app?.name}</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-600">Quota</span>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Quota</h1>
        <button onClick={() => setShowCreateModal(true)} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          + New Quota
        </button>
      </div>

      <p className="text-gray-600 mb-6">Quota are numeric limits (e.g., max projects, API calls per month) that can be set per pricing level.</p>

      {quotaList.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <p className="text-gray-500 mb-4">No quota defined yet.</p>
          <button onClick={() => setShowCreateModal(true)} className="text-blue-600 hover:text-blue-800">Create a quota</button>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          {quotaList.map((q) => (
            <div key={q.id} className="p-4 flex justify-between items-center">
              <div>
                <p className="font-medium">{q.name}</p>
                <p className="text-sm text-gray-500">{q.key}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Type: {q.quota_type === 'capacity' ? 'Capacity (resource count)' : 'Consumable (usage-based)'}
                </p>
                {q.description && <p className="text-sm text-gray-600 mt-1">{q.description}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditingQuotum(q)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                <button onClick={() => handleDelete(q)} className="text-red-600 hover:text-red-800 text-sm">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <QuotumModal appId={appId} onClose={() => setShowCreateModal(false)} onSaved={() => { setShowCreateModal(false); loadData(); }} />
      )}

      {editingQuotum && (
        <QuotumModal appId={appId} quotum={editingQuotum} onClose={() => setEditingQuotum(null)} onSaved={() => { setEditingQuotum(null); loadData(); }} />
      )}
    </div>
  );
}

function QuotumModal({ appId, quotum, onClose, onSaved }) {
  const [key, setKey] = useState(quotum?.key || '');
  const [name, setName] = useState(quotum?.name || '');
  const [description, setDescription] = useState(quotum?.description || '');
  const [quotaType, setQuotaType] = useState(quotum?.quota_type || 'consumable');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      if (quotum) {
        await quota.update(appId, quotum.id, { key, name, description, quota_type: quotaType });
      } else {
        await quota.create(appId, { key, name, description, quota_type: quotaType });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save quota');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={quotum ? 'Edit Quota' : 'New Quota'} size="md">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
          <input type="text" value={key} onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g., max_projects" required />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="e.g., Max Projects" required />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Quota Type</label>
          <select
            value={quotaType}
            onChange={(e) => setQuotaType(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="consumable">Consumable (usage-based)</option>
            <option value="capacity">Capacity (resource count)</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {quotaType === 'consumable'
              ? 'Usage accumulates over billing period, resets at renewal (e.g., AI credits, API calls)'
              : 'Current count of resources, checked by consuming app (e.g., users, projects, monitors)'}
          </p>
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2" rows={2} />
        </div>
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
          <button type="submit" disabled={saving || !key || !name} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : (quotum ? 'Save' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
